/**
 * One run at a time for a user action that sends a request.
 *
 * React state (`submitting`) only updates on the next render, so a double
 * click (or Enter plus click) inside one frame read the stale `false` twice
 * and sent the request twice. The flag here flips synchronously and is
 * released in `finally`, so a later attempt after an error still works.
 */
export interface SingleFlight {
  /** Runs `task` unless one is already running; returns undefined when skipped. */
  run<T>(task: () => Promise<T>): Promise<T | undefined>;
  readonly busy: boolean;
}

export function createSingleFlight(): SingleFlight {
  let busy = false;
  return {
    async run(task) {
      if (busy) return undefined;
      busy = true;
      try {
        return await task();
      } finally {
        busy = false;
      }
    },
    get busy() {
      return busy;
    },
  };
}
